/**
 * The rules behind enrolling one contact in one sequence, and ending it.
 *
 * Named for the concern rather than the component, like lifecycleRules.ts
 * beside LifecyclePanel.tsx and sequenceRules.ts beside Sequences.tsx. The
 * screen is the lifecycle panel: enrollment is not a place you go, it is
 * something you do to a person, and the person's page is where you are.
 *
 * WHY THE RULES ARE HERE AND NOT IN THE COMPONENT
 * -----------------------------------------------
 * The `marketing` vitest project globs plain `.ts` and runs in Node; `app`
 * runs in real Chromium and costs a ~130 MB Playwright download. Every rule
 * below decides either what is WRITTEN to `enrollments` and `enrollment_steps`
 * or whether a person is told the truth about why no tasks are appearing, and
 * none of it needs a DOM. It should not sit one browser install away from
 * being skipped.
 *
 * WHY ENROLLMENT IS MANUAL, ONE CONTACT AT A TIME
 * ----------------------------------------------
 * Nothing in this system auto-enrolls, and 052 says why: auto-enrollment is how
 * one person lands in two sequences at once, the failure Dittofeed's
 * four-part re-entry rule exists to prevent (exit the journey, exit the
 * segment, re-enter the segment, and the journey allows re-entry). Dittofeed
 * calls that Segment Entry — at most one running instance per user — as
 * against Event Entry, one instance per event. This is Segment Entry with the
 * fourth condition supplied by a human deciding, which is the one form of
 * re-entry that cannot fire twice by accident.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE TRAP THIS FILE EXISTS TO CLOSE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * AN ENROLLMENT WITH NO STEPS IS DEAD, AND IT LOCKS THE CONTACT OUT OF EVERY
 * OTHER SEQUENCE.
 *
 * Since 078 a contact walks its OWN materialized plan in `enrollment_steps`
 * rather than arithmetic against the shared `sequence_steps` template, and the
 * completion rule is "are there any pending steps left?" — which is what makes
 * the sweep correct after an insert, a removal or a reorder. Its cost is that
 * an enrollment created WITH NO STEPS is indistinguishable from one that has
 * finished: the first nightly run marks it `completed` with ended_reason
 * 'last_step', and the record then claims somebody was worked all the way
 * through a cadence they never received a word of.
 *
 * It does not fail loudly and it does not fail quietly-but-harmlessly:
 * `enrollments_one_active` is UNIQUE (contact_id) WHERE status IN
 * ('active','paused') -- widened by 074 and verified against the live index
 * 2026-09-04 -- so until that sweep runs the empty row holds the only live
 * slot the contact has. No error, no task, and nothing on any screen to
 * notice.
 *
 * THIS WAS REACHABLE FROM THE PANEL AND WAS MEASURED IN PRODUCTION. The
 * previous startEnrollment() created an `enrollments` row through the data
 * provider and no steps at all, and a step-less enrollment came out of the
 * sweep marked completed. Two consequences run through everything below:
 *
 *   - startEnrollment() writes through public.enroll_contact(), which creates
 *     the enrollment AND its whole plan in one transaction, or neither. Two
 *     data-provider calls cannot span two tables, and the row left behind when
 *     the second one fails is worse than no row at all. 077 is the argument in
 *     full; its refusal of an empty plan is this file's refusal, one layer
 *     down, where nothing can get past it.
 *   - canEnroll() refuses any sequence with no steps to seed a plan from —
 *     which today is EVERY sequence, because a new one has none and there are
 *     none in the database at all. The default state of a sequence is exactly
 *     the state that produces the dead row.
 * ══════════════════════════════════════════════════════════════════════════
 */

import type { DataProvider, Identifier } from "ra-core";

import { materializeDates, shiftAfterPause } from "./enrollmentSchedule";
import type { StepWrite } from "./planRules";
import { numberingGap } from "./sequenceRules";
import {
  OVER_ENROLLMENT_STATUSES,
  type ContactHealth,
  type Enrollment,
  type EnrollmentStep,
  type Sequence,
  type SequenceStep,
} from "./types";

/* ── starting one ──────────────────────────────────────────────────────── */

export type EnrollRefusalKind =
  | "closed"
  | "already"
  | "no-sequence"
  | "retired"
  | "stepless"
  | "gap";

export type EnrollRefusal = {
  kind: EnrollRefusalKind;
  /** The whole reason, in a sentence, for the screen. */
  reason: string;
  /**
   * The sequence that is the blocker, when the blocker IS a sequence. The
   * panel turns this into a link to its detail page: this codebase's rule is
   * that anything blocking an action links to the place the block is lifted,
   * and "this sequence has no steps" with no way to reach it is a dead end.
   */
  sequence: Sequence | null;
};

export type EnrollCheck =
  | { ok: true; firstStep: SequenceStep }
  | { ok: false; refusal: EnrollRefusal };

/**
 * May this contact be enrolled in this sequence, and if not, why not?
 *
 * THE ORDER OF THE CHECKS IS THE GENERATOR'S OWN ORDER, not a preference.
 * advance_enrollments() ends the enrollments of terminal-stage contacts in step
 * 1, BEFORE it generates anything, writing ended_reason 'stage_terminal'. So
 * on a closed record even an enrollment that already exists is one nightly run
 * from being ended, and answering "you already have one" would be advice
 * about a row that will not survive the night. The closed record is the more
 * durable fact, so it is named first.
 *
 * Every refusal carries a sentence rather than a code, because every one of
 * them is displayed. A refusal a person cannot act on is a disabled button.
 */
export function canEnroll(
  health: ContactHealth | null | undefined,
  activeEnrollment: Enrollment | null | undefined,
  sequence: Sequence | null | undefined,
  steps: readonly SequenceStep[],
): EnrollCheck {
  if (health?.is_terminal)
    return {
      ok: false,
      refusal: {
        kind: "closed",
        sequence: null,
        reason: `This record is closed as ${health.track} · ${health.lifecycle_stage}. Enrolling would create a row tonight's run ends straight away with ended_reason 'stage_terminal' — a terminal stage ends enrollments before it generates anything.`,
      },
    };

  if (activeEnrollment)
    return {
      ok: false,
      refusal: {
        /*
         * ONE `kind` FOR BOTH, and the sentence carries the difference.
         * `kind` exists to branch the PRESENTATION, and both cases present
         * identically -- no second picker, one sentence saying why. A screen
         * that wanted to offer Resume reads `activeEnrollment.status`, which
         * is the fact itself rather than a second copy of it.
         */
        kind: "already",
        sequence: null,
        reason:
          activeEnrollment.status === "paused"
            ? "This contact is already in a sequence — it is paused, not finished. A pause keeps the slot: enrollments_one_active is unique on contact_id where the status is active OR paused, so a second enrollment is refused by the database. Resume this one, or end it with a reason, before starting another."
            : "This contact is already in a sequence, and the database allows exactly one live one — enrollments_one_active is unique on contact_id where the status is active or paused. End the current one, with a reason, before starting another.",
      },
    };

  if (!sequence)
    return {
      ok: false,
      refusal: {
        kind: "no-sequence",
        sequence: null,
        reason: "Choose a sequence to enroll this contact in.",
      },
    };

  /*
   * A retired sequence is not offered by the picker either, but a picker is a
   * suggestion and this is what the write goes through -- the same two-guard
   * arrangement stagePatch() has in lifecycleRules.ts. is_active is the only
   * retirement path this schema has (nothing may be deleted, because
   * enrollments.sequence_id references it), so starting somebody on a retired
   * cadence would defeat the one control an author has over that.
   */
  if (!sequence.is_active)
    return {
      ok: false,
      refusal: {
        kind: "retired",
        sequence,
        reason: `${sequence.name} is retired. Retiring is the only way to stop a sequence being used — nothing here is ever deleted — so nobody new starts on one.`,
      },
    };

  if (steps.length === 0)
    return {
      ok: false,
      refusal: {
        kind: "stepless",
        sequence,
        reason: `${sequence.name} has no steps, so there is no plan to seed the enrollment from. The row would have no pending step, which is indistinguishable from a finished one: tonight's run would mark it completed with ended_reason 'last_step' — the record claiming this contact was worked through a cadence they never received a word of — and until it did, that row would hold the one active slot they have. Add a step first.`,
      },
    };

  /*
   * A GAP NO LONGER BREAKS THE GENERATOR -- AND IS STILL WORTH REFUSING.
   *
   * It used to be fatal: the generator walked `step_no = current_step + 1`,
   * so a sequence numbered 1, 3 completed every enrollment at step 1 and filed
   * the ending as 'last_step', claiming the person finished a cadence they
   * were dropped out of. Migration 078 removed that mechanism entirely --
   * steps are materialized onto the enrollment and the sweep reads whichever
   * are pending, so 1, 3 now seeds two steps and both fire. db/tests
   * enrollment_steps 17a asserts exactly that, with a plan numbered 1, 3, 7.
   *
   * So this refusal is no longer about the generator. It is about AUTHORING:
   * a hole means a step was almost certainly deleted by hand and nobody
   * noticed, and enrolling somebody on a cadence that is missing a piece of
   * its argument is worth stopping to look at. sequence_steps is an ordinary
   * table and one hand-run DELETE is all it takes.
   *
   * The reason string says that, and must keep saying that. Describing a
   * mechanism that no longer exists is worse than saying nothing: the person
   * reading it goes looking for a failure they will never find.
   */
  const gap = numberingGap(steps);
  if (gap != null)
    return {
      ok: false,
      refusal: {
        kind: "gap",
        sequence,
        reason: `${sequence.name} has no step ${gap}, so a step was probably lost while it was being written. Check the numbering before anyone is enrolled on it.`,
      },
    };

  // Guaranteed by the gap check above: contiguous from 1 means there is a 1.
  return { ok: true, firstStep: steps.find((s) => s.step_no === 1)! };
}

export type EnrollArgs = {
  health: ContactHealth | null | undefined;
  activeEnrollment: Enrollment | null | undefined;
  sequence: Sequence | null | undefined;
  steps: readonly SequenceStep[];
};

/**
 * One step of a plan, in the shape `enroll_contact` accepts and no other.
 *
 * TRANSCRIBED FROM 077, WHICH REFUSES AN UNKNOWN KEY RATHER THAN IGNORING IT.
 * That refusal is the reason this type is written out by hand instead of
 * derived from SequenceStep: a step that carried one extra field would be a
 * plan the database silently did not store, and a caller told nothing.
 *
 * step_no, due_at and offset_days are NOT NULL columns, so they are required
 * here too — a plan missing one of them is not a plan with a gap, it is a plan
 * that cannot be written. `status`, `skipped_reason` and `edited` are absent
 * on purpose: they are what a plan ACQUIRES, and nothing is generated, skipped
 * or edited at the moment it is created.
 */
export type PlannedStep = {
  step_no: number;
  /** The real date, ISO. Editable afterwards — that is the feature. */
  due_at: string;
  /** The intent the date came from, kept beside it. */
  offset_days: number;
  /** Null is legitimate: a bare task with no asset behind it. */
  content_id: number | null;
  /** Null means ask the asset. */
  task_type: string | null;
  source: "template" | "added";
};

/**
 * The one write on this screen that does not go through the data provider.
 *
 * IT IS A PARAMETER, TYPED, for the reason cancelOutbound()'s RPC is one: the
 * provider is built once outside React and cannot be swapped per test, so
 * routing this through it would trade a one-second Node assertion for a mocked
 * module or a browser — and this is the write whose failure mode is a contact
 * silently never nurtured. It is the last thing that should be provable only
 * where somebody might skip it.
 */
export type EnrollRpc = (args: {
  p_contact_id: number;
  /** Provenance only, and nullable in 077 for a hand-built plan. */
  p_sequence_id: number | null;
  p_steps: PlannedStep[];
}) => Promise<{ data: unknown; error: { message: string } | null }>;

/** The real one. Lazily imported so no test that never enrolls needs a client. */
export const enrollViaSupabase: EnrollRpc = async (args) => {
  const { getSupabaseClient } = await import("../providers/supabase/supabase");
  return getSupabaseClient().rpc("enroll_contact", args);
};

/**
 * A template's steps, as the plan this contact will actually walk.
 *
 * THE DATES COME FROM materializeDates(), NEVER FROM A SECOND COPY OF THAT
 * SUM. `offset_days` counts from the PREVIOUS step, so [0, 3, 4] is days 0, 3
 * and 7; read straight it is 0, 3 and 4, the last message lands three days
 * early on top of the one before it, and nothing anywhere reports it. That
 * function also owns the jitter and the "nothing at cumulative offset 0 is
 * moved" rule, which is why `rand` travels through here rather than being
 * resolved on the way in.
 *
 * `channel` AND `source_step_id` ARE NOT MAPPED, and that is the half of this
 * function worth reading. Both were columns on `enrollment_steps` until 075 and
 * `channel` is still a column on SequenceStep, so spreading the template row
 * is the obvious way to write this — and 077 refuses the result rather than
 * dropping the field, deliberately, because a dropped field means a plan was
 * saved that is not the plan that was composed. A step IS a content asset now,
 * and the asset answers both questions.
 *
 * `source` IS STATED rather than left to default. 077 would infer 'template'
 * from a non-null p_sequence_id and would be right here — but only a caller
 * knows which steps a person added by hand to an otherwise templated plan, so
 * the file that knows is the file that says.
 */
function seededPlan(
  steps: readonly SequenceStep[],
  now: Date,
  rand?: () => number,
): PlannedStep[] {
  // Keyed by step_no because materializeDates() SORTS, and pairing its output
  // back up by index would silently attach every date to the wrong asset the
  // first time a template arrived out of order. canEnroll() has already refused
  // any numbering that is not 1..n -- a duplicate reads as a hole to
  // numberingGap() -- so every lookup below resolves.
  const template = new Map(steps.map((s) => [s.step_no, s]));

  return materializeDates(steps, now, rand).map(({ step_no, due_at }) => {
    const from = template.get(step_no)!;
    return {
      step_no,
      due_at: due_at.toISOString(),
      offset_days: from.offset_days,
      content_id: from.content_id,
      task_type: from.task_type,
      source: "template",
    };
  });
}

/**
 * Enroll, or throw the refusal.
 *
 * THE ENROLLMENT AND ITS PLAN ARE ONE CALL because they must be one
 * transaction. `dataProvider.create` on `enrollments` followed by a second
 * write to `enrollment_steps` is two requests with a network between them, and
 * the row the second one leaves behind when it fails is an enrollment with no
 * pending step — completed by the next nightly run, with the contact silently
 * never nurtured. That is not a smaller version of the failure; it is the
 * failure, which is why 077 exists and why nothing here writes `enrollments`
 * directly.
 *
 * IT RE-RUNS canEnroll RATHER THAN TRUSTING THE CALLER, AND BEFORE THE CALL.
 * The panel disables its button from the same check, but a disabled button is
 * a suggestion and this is what reaches the database. 077 would refuse a
 * step-less plan too — that is the point of it — but its raise cannot name
 * WHICH sequence is empty or say that the fix is to add a step to it, because
 * it was never told a sequence was involved. Both sentences are true; only one
 * of them is on the screen the person is looking at.
 *
 * A REFUSAL FROM THE DATABASE IS REPORTED VERBATIM. 077's messages are written
 * to be read by a person, and `enrollments_one_active` names itself. A
 * paraphrase would send somebody to the network tab for something already
 * spelled out.
 */
export async function startEnrollment(
  rpc: EnrollRpc,
  contactId: Identifier,
  args: EnrollArgs,
  now: Date = new Date(),
  rand?: () => number,
): Promise<void> {
  const check = canEnroll(
    args.health,
    args.activeEnrollment,
    args.sequence,
    args.steps,
  );
  if (!check.ok) throw new Error(check.refusal.reason);

  const { error } = await rpc({
    // Identifier is string | number; `bigint` is not either of them.
    p_contact_id: Number(contactId),
    // Non-null because canEnroll() refuses a missing sequence above. Null is
    // 077's shape for a hand-built plan, which nothing composes yet.
    p_sequence_id: args.sequence!.id,
    p_steps: seededPlan(args.steps, now, rand),
  });
  if (error) throw new Error(error.message);
}

/* ── ending one ────────────────────────────────────────────────────────── */

export type EndReasonCheck =
  | { ok: true; reason: string }
  | { ok: false; message: string };

/**
 * Can this be the reason an enrollment stopped?
 *
 * A BLANK REASON IS REFUSED HERE AND BY THE DATABASE.
 * `enrollments_ended_has_reason` is CHECK (status = 'active' OR ended_reason
 * IS NOT NULL), so an unaccountable ending cannot be stored at all -- but a
 * constraint error in a toast teaches nothing, and the point is not to pass
 * the check. An ending nobody can account for is the same defect as a lead
 * that just went quiet: six months later the record says the sequence stopped
 * and nothing says why, so nobody can tell a considered stop from a mistake.
 *
 * There is deliberately no optional path. An empty string would satisfy the
 * constraint while defeating it.
 */
export function validateEndReason(
  raw: string | null | undefined,
): EndReasonCheck {
  const reason = (raw ?? "").trim();
  if (!reason)
    return {
      ok: false,
      message:
        "Say why this is ending. An enrollment that stops with no reason is indistinguishable from one nobody meant to stop.",
    };
  return { ok: true, reason };
}

/**
 * Stop an enrollment by hand.
 *
 * `ended`, NOT `completed`. The generator writes `completed` with
 * ended_reason 'last_step' when a cadence runs out of steps, and the
 * difference between the two is the difference between somebody finishing the
 * plan and somebody being taken off it. Writing `completed` here would make
 * the record claim the person was worked all the way through.
 *
 * All three columns move together, because the constraint says two of them
 * must and the third is the only record of when.
 */
export async function endEnrollment(
  dataProvider: DataProvider,
  enrollment: Enrollment,
  reason: string,
  now: Date = new Date(),
): Promise<void> {
  const check = validateEndReason(reason);
  if (!check.ok) throw new Error(check.message);

  await dataProvider.update("enrollments", {
    id: enrollment.id,
    previousData: enrollment,
    data: {
      status: "ended",
      ended_reason: check.reason,
      ended_at: now.toISOString(),
    },
  });
}

/* ── pausing the drip, and resuming it ─────────────────────────────────── */

/**
 * ══════════════════════════════════════════════════════════════════════════
 * PAUSING THE ENROLLMENT IS NOT PAUSING THE CONTACT, AND ONE CONTROL COULD
 * NEVER HAVE SERVED BOTH.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `contact_lifecycle.paused_until` silences EVERYTHING about a person — the
 * drip and their own follow-up reminder — and takes them out of
 * `contacts_no_next_action` entirely (`is_paused` is that view's only
 * exclusion). It is permission to stop looking.
 *
 * `enrollments.status = 'paused'` stops the automated drip and leaves the
 * follow-up clock exactly where it was. That is the "this lead went warm"
 * case: you are chasing them by hand and do not want a template email landing
 * in the middle of the conversation. They stay overdue if you stop.
 *
 * The design doc took this as a decision rather than an accident — "a
 * separate pause on the enrollment, beside the existing contact pause" — and
 * 074 built it. Until now nothing wrote it.
 *
 * WHAT A PAUSE ACTUALLY DOES TO THE PLAN, read from the live function
 * 2026-09-04 rather than from a migration file:
 *
 *     where es.status = 'pending' and es.due_at <= now() and e.status = 'active'
 *
 * `paused` simply fails that predicate. No task is written and NOTHING MARKS
 * THE STEP — so a due step stays `pending` with its date in the past for as
 * long as the pause runs. THAT is the suspension. It is not a skip, and it is
 * also not free: the day the enrollment goes back to `active`, every one of
 * those steps is `due_at <= now()` and the next nightly run fires the whole
 * remainder of the cadence in one morning. Which is why resuming shifts them,
 * and why resumeEnrollment() refuses to resume a row it cannot measure.
 */

export type PauseVerdict = { ok: true } | { ok: false; reason: string };

/**
 * May this enrollment's drip be stopped?
 *
 * PAUSING A PAUSED ONE IS REFUSED, and not for tidiness: the write sets
 * `paused_at = now()`, which is the ONLY record of when the suspension began
 * and the only input to the resume shift. A second pause would overwrite it
 * with today, the shift would come out as zero, and every step that sat due
 * through the pause would fire at once — the exact failure the shift exists
 * to prevent, reintroduced by a double click.
 */
export function pauseVerdict(
  enrollment: Enrollment | null | undefined,
): PauseVerdict {
  if (!enrollment)
    return {
      ok: false,
      reason: "There is no live enrollment here to pause.",
    };
  if (enrollment.status === "paused")
    return {
      ok: false,
      reason:
        "This enrollment is already paused. Pausing it again would overwrite paused_at, and that timestamp is the only record of how long the drip has been stopped — without it a resume cannot tell how far to move the remaining steps.",
    };
  if (enrollment.status !== "active")
    return {
      ok: false,
      reason: `This enrollment is over — it is ${enrollment.status}. Pausing is for a sequence that is still running; start a new one instead.`,
    };
  return { ok: true };
}

/** May this enrollment's drip be started again? */
export function resumeVerdict(
  enrollment: Enrollment | null | undefined,
): PauseVerdict {
  if (!enrollment)
    return { ok: false, reason: "There is no enrollment here to resume." };
  if (enrollment.status !== "paused")
    return {
      ok: false,
      reason: `Only a paused enrollment can be resumed, and this one is ${enrollment.status}.`,
    };
  if (!enrollment.paused_at)
    return {
      ok: false,
      reason:
        "This enrollment is paused but has no paused_at, so there is no way to tell how long the drip has been stopped. Resuming would leave every pending step dated before the pause — advance_enrollments() tests due_at <= now(), so the next run would fire the whole remainder of the cadence at once. Retime the steps by hand instead.",
    };
  return { ok: true };
}

/** What resuming will do, worked out before anything is written. */
export type ResumePlan = {
  /** Whole days of pause, rounded down. What the sentence on screen says. */
  days: number;
  /** The updates. Empty when the pause moved nothing. */
  writes: StepWrite[];
  /** Every step whose date changes, with both dates, for the preview. */
  moved: { step: EnrollmentStep; from: string; to: string }[];
  /**
   * The steps that do NOT move, with the reason. Listed rather than omitted:
   * silence about an exclusion is the same defect as silence about a lead.
   */
  frozen: { step: EnrollmentStep; reason: string }[];
};

const MS_PER_DAY = 86_400_000;

/**
 * Where every step lands once the drip starts again.
 *
 * THE ARITHMETIC IS shiftAfterPause()'s, NOT A SECOND COPY OF IT. That
 * function has been tested since the day 074 landed and had no caller, and it
 * owns the two rules that matter: every PENDING step moves by the same delta
 * so the gaps the template described survive, and a negative delta is
 * clamped rather than applied, so a wrong clock cannot make a paused
 * enrollment more urgent than it was.
 *
 * A STEP WHOSE DATE DOES NOT MOVE IS NOT WRITTEN. Each write sets
 * `edited: true`, and a no-op write would spend that flag for nothing —
 * turning propagation off across a whole plan in exchange for rewriting a
 * date to itself.
 *
 * WHY A MOVED STEP IS AN EDITED STEP. `edited` is propagation's second
 * condition: canPropagate() refuses a step somebody has changed, because "the
 * sequence no longer speaks for it". Nobody typed into these — but a person
 * paused and resumed this contact, and a later template edit recomputing
 * `due_at` from the template would pull the step back to where it was before
 * the pause and silently undo that decision. planStepInsertion() marks its
 * displaced steps for exactly this reason; this is the same argument.
 */
export function planResume(
  enrollment: Enrollment,
  steps: readonly EnrollmentStep[],
  resumedAt: Date = new Date(),
): ResumePlan {
  const pausedAt = new Date(enrollment.paused_at ?? resumedAt.toISOString());
  const shifted = shiftAfterPause(
    steps.map((s) => ({ due_at: new Date(s.due_at), status: s.status })),
    pausedAt,
    resumedAt,
  );

  const writes: StepWrite[] = [];
  const moved: ResumePlan["moved"] = [];
  const frozen: ResumePlan["frozen"] = [];

  steps.forEach((step, i) => {
    if (step.status !== "pending") {
      frozen.push({
        step,
        reason:
          step.status === "generated"
            ? "already turned into a task — its date is shared with that task, and this schema gives every fact one writer"
            : `skipped${step.skipped_reason ? ` — ${step.skipped_reason}` : ""}, and that reason was written on a particular day`,
      });
      return;
    }
    const to = shifted[i].toISOString();
    if (to === step.due_at) return;
    moved.push({ step, from: step.due_at, to });
    writes.push({ id: step.id, data: { due_at: to, edited: true } });
  });

  return {
    days: Math.floor(
      Math.max(0, resumedAt.getTime() - pausedAt.getTime()) / MS_PER_DAY,
    ),
    writes,
    moved,
    frozen,
  };
}

/**
 * Stop the drip. One write, and no reason required.
 *
 * `enrollments_ended_has_reason` exempts both `active` and `paused`, which is
 * the schema saying a pause is not an ending: nothing has been given up, so
 * there is nothing to account for. Writing `ended_reason` here would be
 * wrong rather than merely redundant — that column is what the panel prints
 * to explain why a sequence STOPPED, and this one has not.
 */
export async function pauseEnrollment(
  dataProvider: DataProvider,
  enrollment: Enrollment,
  now: Date = new Date(),
): Promise<void> {
  const verdict = pauseVerdict(enrollment);
  if (!verdict.ok) throw new Error(verdict.reason);

  await dataProvider.update("enrollments", {
    id: enrollment.id,
    previousData: enrollment,
    data: { status: "paused", paused_at: now.toISOString() },
  });
}

/**
 * Start the drip again, moving the whole remaining plan out by the pause.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE STEPS MOVE FIRST. THE ENROLLMENT GOES ACTIVE LAST.
 * ══════════════════════════════════════════════════════════════════════════
 * There is no transaction around these, so the order is the only protection
 * there is, and the two failure modes are not symmetric:
 *
 *   steps then enrollment — an interruption leaves a PAUSED enrollment whose
 *     pending steps have already moved. Nothing generates, because `paused`
 *     fails the generator's predicate. The person presses Resume again and it
 *     completes; the second shift is measured from the same unchanged
 *     `paused_at`, so it lands the steps in the same place. Harmless, and
 *     idempotent by accident of the arithmetic rather than by luck.
 *
 *   enrollment then steps — an interruption leaves an ACTIVE enrollment whose
 *     pending steps are all still dated before the pause. `due_at <= now()`
 *     is true of every one of them, so the next nightly run writes the entire
 *     remainder of the cadence as tasks in one morning. That is the failure
 *     the shift exists to prevent, arriving through the write order instead.
 *
 * `paused_at` IS CLEARED. Left set, a second pause-and-resume would measure
 * its delta from the first pause and shift the plan by the two periods
 * combined.
 *
 * NOTHING HERE INVALIDATES, matching planRules.ts: these go through
 * `useDataProvider()`, which bypasses react-admin's mutation hooks. The
 * caller invalidates once, with invalidateLifecycle(), after every write has
 * landed — doing it per write would refetch the plan mid-shift and paint a
 * state that is nobody's answer.
 */
export async function resumeEnrollment(
  dataProvider: DataProvider,
  enrollment: Enrollment,
  steps: readonly EnrollmentStep[],
  now: Date = new Date(),
): Promise<ResumePlan> {
  const verdict = resumeVerdict(enrollment);
  if (!verdict.ok) throw new Error(verdict.reason);

  const plan = planResume(enrollment, steps, now);

  for (const write of plan.writes)
    await dataProvider.update("enrollment_steps", {
      id: write.id,
      previousData: steps.find((s) => s.id === write.id),
      data: write.data,
    });

  await dataProvider.update("enrollments", {
    id: enrollment.id,
    previousData: enrollment,
    data: { status: "active", paused_at: null },
  });

  return plan;
}

/* ── what the panel says about one ─────────────────────────────────────── */

/**
 * The two endings the generator writes, in words.
 *
 * `ended_reason` holds both machine codes and a human's own sentence, so the
 * panel has to be able to tell them apart. Anything not in here came from a
 * person and is shown verbatim -- quoting somebody's words back as an
 * explanation is exactly right; paraphrasing a code as one would not be.
 */
export const MACHINE_ENDINGS: Record<string, string> = {
  last_step: "ran out of steps — the whole cadence was worked",
  stage_terminal:
    "ended automatically when this record was closed, because a terminal stage ends enrollments before anything is generated",
};

export function describeEnding(
  enrollment: Pick<Enrollment, "status" | "ended_reason">,
): string {
  const reason = enrollment.ended_reason;
  if (!reason) {
    /*
     * `paused` IS LISTED HERE BECAUSE THE CONSTRAINT EXEMPTS IT.
     * `enrollments_ended_has_reason` is CHECK (status IN ('active','paused')
     * OR ended_reason IS NOT NULL), so a paused row has no ended_reason BY
     * CONSTRUCTION. Falling through to "this should not be possible" would
     * put the screen's loudest alarm on the single most ordinary state a
     * suspended enrollment can be in.
     *
     * Anything else with no reason really did get past a CHECK constraint,
     * and would be the most important thing on the page if it happened.
     */
    if (enrollment.status === "active") return "still running";
    if (enrollment.status === "paused")
      return "paused — the drip is stopped and the plan is keeping its place";
    return "no reason recorded — this should not be possible";
  }
  const known = MACHINE_ENDINGS[reason];
  return known ?? `stopped by hand — “${reason}”`;
}

export type EnrollmentProgress = {
  /** Steps whose status is `generated`. NOT how many are behind us. */
  done: number;
  /** How many steps THIS enrollment has, skipped ones included. */
  total: number;
  /**
   * The step 078 will generate next: the first one still `pending`, and the
   * row itself, because the panel prints its `due_at`. Null when there is no
   * pending step left, which on an active enrollment is the state the next
   * nightly pass turns into `completed`.
   */
  next: EnrollmentStep | null;
};

/**
 * How far through its own plan this enrollment is.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * IT READS THE CONTACT'S STEPS. THERE IS NO COUNTER AND THERE IS NO TEMPLATE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * This function used to take `enrollments.current_step` and the SEQUENCE's
 * steps, and every number it produced is now wrong in a different way:
 *
 *   - `current_step` is dead. Measured against the live database: 077 leaves
 *     it at its default of 0 and 078 never touches it. So the panel reported
 *     "0 done, next is step 1" for every enrollment created since, no matter
 *     how many steps had actually fired -- and once 079 drops the column it
 *     would have reported "all N steps have run" instead, which is the same
 *     falsehood pointing the other way.
 *   - The template's length is a DIFFERENT NUMBER the moment anybody adds or
 *     removes a step for this person, and that divergence is the entire point
 *     of materializing the plan. Reading `sequence_steps` prints "step 4 of 3"
 *     the first time somebody uses the freedom the feature exists to give.
 *
 * Neither guard caught it. `Enrollment` is hand-written here rather than
 * generated from the schema, so `tsc` had nothing to check the column against,
 * and the UI fixture supplied `current_step` as an object literal, so the
 * browser test was green against a panel that could not tell the truth.
 *
 * THE THREE RULES, AND EACH ONE IS A WAY THE OLD SHAPE WAS WRONG:
 *
 *   done   counts `generated`. A position says how many rows we have walked
 *          past; only the status says how many produced anything.
 *   next   is the first PENDING step by `step_no` -- a search, never
 *          `done + 1`. `step_no` is mutable and 078 does no arithmetic on it,
 *          so 1, 3, 7 is a legal plan and positional logic finds nothing in
 *          it while five steps are still to run.
 *   total  is this enrollment's own row count.
 *
 * A SKIPPED STEP IS NEITHER DONE NOR NEXT, AND MUST NOT HOLD THE ENROLLMENT
 * OPEN. Somebody set it aside on a day and wrote down why: counting it as done
 * claims we sent something we deliberately did not, and offering it as next
 * puts it back on the plan it was taken off. It stays in `total` because it is
 * still part of the plan and still on the record -- and because 078 completes
 * on "no PENDING steps left", so a total that hid it would disagree with the
 * job about the same enrollment on the same night.
 *
 * NOTHING HERE SUMS `offset_days`. The old version reported "day N of the
 * cadence" from stepSchedule(), which was right about a template: the column
 * counts from the PREVIOUS step, so [0, 3, 4] is days 0, 3 and 7. A
 * materialized step carries `due_at`, a real editable date that the jitter and
 * every hand edit since have already moved. Re-deriving a day from the offsets
 * beside it would be a second answer to "when does this land", and the one
 * nobody edited would be the one on screen.
 */
export function progress(steps: readonly EnrollmentStep[]): EnrollmentProgress {
  const pending = [...steps]
    .filter((s) => s.status === "pending")
    // Sorted here rather than trusted from the caller: the panel asks
    // PostgREST for step_no ASC, but "the first pending step" is this
    // function's claim to make, and a plan is reorderable by design.
    .sort((a, b) => a.step_no - b.step_no);

  return {
    done: steps.filter((s) => s.status === "generated").length,
    total: steps.length,
    next: pending[0] ?? null,
  };
}

/* ── suspension: the thing a status column cannot say ──────────────────── */

export type Suspension = {
  suspended: boolean;
  /** The running pause, or null. A date already passed is not one. */
  pausedUntil: string | null;
  pauseReason: string | null;
  /** The commitment still ahead, or null. One already met is not one. */
  commitmentAt: string | null;
  commitmentNote: string | null;
};

/**
 * Is this enrollment being held, and by which mechanism?
 *
 * WHY THIS EXISTS AT ALL, AND IT IS THE POINT OF THE WHOLE SCREEN.
 * A live `paused_until` or a future `commitment_at` makes advance_enrollments()
 * skip an enrollment entirely: no task, and the due step LEFT PENDING with its
 * `due_at` in the past, so it fires the instant the hold lifts rather than
 * being skipped. 078 says so in as many words -- the suppressors have no
 * clause of their own in the update that marks steps generated, precisely
 * because leaving the step pending IS the suspension. The row stays `active`
 * throughout. A panel that read the status
 * column and said "active" would be lying by omission about why no tasks are
 * appearing -- and unexplained silence is the exact failure this whole system
 * was built to end. Saying "suspended, by this, until then" is the difference
 * between a system that stopped and a system that stopped for a reason.
 *
 * THE PREDICATES ARE THE GENERATOR'S, TRANSCRIBED. It suppresses on
 *
 *     and (cl.paused_until is null or cl.paused_until < current_date)
 *     and (cl.commitment_at is null or cl.commitment_at <= now())
 *
 * so a pause holds while paused_until >= today -- INCLUSIVE, quiet through
 * the last day, matching contact_health and pauseState() -- and a commitment
 * holds while commitment_at is strictly in the future. Getting either
 * boundary wrong by one makes the panel disagree with the job about the same
 * contact on the same day, which is worse than not showing it.
 *
 * Dates are compared as strings: both sides are YYYY-MM-DD, where
 * lexicographic order is chronological, and new Date("2026-09-14") is UTC
 * midnight -- the 13th at 8pm in New York -- which would expire every pause a
 * day early for anyone west of Greenwich.
 *
 * BOTH CAN HOLD AT ONCE and both are reported. Telling somebody the pause
 * ends on Friday, while a meeting on Monday is also holding the sequence,
 * would be a promise the generator does not keep.
 */
export function suspension(
  health:
    | Pick<
        ContactHealth,
        "paused_until" | "paused_reason" | "commitment_at" | "commitment_note"
      >
    | null
    | undefined,
  today: string,
  now: Date = new Date(),
): Suspension {
  const pausedUntil =
    health?.paused_until && health.paused_until >= today
      ? health.paused_until
      : null;
  const commitmentAt =
    health?.commitment_at && new Date(health.commitment_at) > now
      ? health.commitment_at
      : null;

  return {
    suspended: pausedUntil != null || commitmentAt != null,
    pausedUntil,
    pauseReason: pausedUntil ? (health?.paused_reason ?? null) : null,
    commitmentAt,
    commitmentNote: commitmentAt ? (health?.commitment_note ?? null) : null,
  };
}

/* ── picking one out of a list ─────────────────────────────────────────── */

/**
 * The one LIVE enrollment, if there is one — `active` or `paused`.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A PAUSED ENROLLMENT IS LIVE. THE INDEX SAYS SO, AND crm#18 IS WHAT IT COST
 * TO DISAGREE WITH IT.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * This function matched `status === 'active'` alone, which was true of the
 * index until 074 widened it to `where status in ('active','paused')` — a
 * pause KEEPS the contact's slot, deliberately, so that a suspended person
 * cannot be enrolled a second time mid-sequence (052, from Dittofeed's
 * four-part re-entry rule). Reading only `active` produced three wrongs off
 * one line: the live enrollment vanished from this function, reappeared in
 * pastEnrollments() under "everything that is over", and canEnroll() — which
 * refuses on nothing but this value — then offered an Enroll button that the
 * database answered with a raw 23505.
 *
 * Verified against the live index 2026-09-04, not from the migration file:
 *   CREATE UNIQUE INDEX enrollments_one_active ON public.enrollments
 *     USING btree (contact_id) WHERE (status = ANY (ARRAY['active','paused']))
 *
 * Written as a search rather than `rows[0]` because the partial unique index
 * guarantees at most one LIVE row and says nothing at all about the others:
 * a contact may hold any number of completed and ended ones, which is the
 * whole reason the index is partial. Taking the first row of the list would
 * be reading whatever the sort happened to put on top.
 *
 * The name is kept. `liveEnrollment` would read better and would rename a
 * function four screens call, in a commit whose subject is a behavior change
 * — and a rename that travels with a behavior change is the one a reviewer
 * cannot see past.
 */
export function activeEnrollment(
  enrollments: readonly Enrollment[] | null | undefined,
): Enrollment | null {
  return (
    (enrollments ?? []).find(
      (e) => e.status === "active" || e.status === "paused",
    ) ?? null
  );
}

/**
 * Everything that is over, newest first. The record of what was tried.
 *
 * THE TERMINAL STATUSES ARE NAMED, rather than filtered as `!== 'active'`.
 * That is how `paused` came to be listed as history the day 074 shipped:
 * a negative filter files every status the schema grows next under "over",
 * silently, and the wrongest possible thing to be silently wrong about is
 * whether somebody is still being worked. Named, a sixth status is missing
 * from both this list and activeEnrollment() — which is visible on screen,
 * and is a bug somebody reports rather than one nobody can see.
 */
export function pastEnrollments(
  enrollments: readonly Enrollment[] | null | undefined,
): Enrollment[] {
  return (enrollments ?? [])
    .filter((e) =>
      (OVER_ENROLLMENT_STATUSES as readonly string[]).includes(e.status),
    )
    .sort((a, b) => ((a.ended_at ?? "") < (b.ended_at ?? "") ? 1 : -1));
}

/**
 * The sequences worth offering, which is not all of them.
 *
 * Retired ones are left out here and refused again by canEnroll(). Stepless
 * ones are NOT left out: they are the default state of every sequence in this
 * database, and hiding them would turn "you cannot enroll anyone yet" into an
 * empty picker that looks broken. They are offered, labeled, and refused
 * with a link to the page where a step can be added.
 */
export function enrollableSequences(
  sequences: readonly Sequence[] | null | undefined,
): Sequence[] {
  return (sequences ?? []).filter((s) => s.is_active);
}
